import React from 'react';
import { motion } from 'framer-motion';
import { Truck, CheckCircle2 } from 'lucide-react';
import vanBorbiconi from '../assets/van_borbiconi.png';

const processList = [
    { title: "L'Estimation", text: "Estimation fine et réaliste de votre bien en fonction du marché local et frontalier. Remise d'un dossier complet.", img: '/steps/step1.png' },
    { title: "Le Mandat", text: "Élaboration du mandat en bonne et due forme formalisant notre engagement mutuel pour la réussite de votre projet.", img: '/steps/step2.png' },
    { title: "Mise en Valeur", text: "Mise en vente sur tous nos supports professionnels (portails, site web, vitrine, réseaux sociaux) avec des visuels soignés.", img: '/steps/step3.png' },
    { title: "Les Visites", text: "Organisation et réalisation des visites exclusivement avec des acquéreurs dont le projet et le financement ont été qualifiés.", img: '/steps/step4.png' },
    { title: "La Négociation", text: "Gestion des offres d'achat et négociation menée avec rigueur dans le parfait intérêt de chaque partie.", img: '/steps/step5.png' },
    { title: "L'Avant-Contrat", text: "Démarches administratives et signature du compromis de vente chez le notaire pour sceller l'accord.", img: '/steps/step6.png' },
    { title: "L'Accompagnement", text: "Suivi du dossier de financement des acquéreurs, démarches administratives, ou éventuels devis travaux.", img: '/steps/step7.png' },
    { title: "L'Acte Final", text: "Ultime visite du bien et accompagnement jusqu'à la remise des clés lors de la signature de l'acte de vente notarié.", img: '/steps/step8.png' },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0 }
};

const fadeIn = {
  hidden: { opacity: 0, y: 0 },
  visible: { opacity: 1, y: 0 }
};

const Process = () => {
    return (
        <div className="bg-[#fcfaf8] min-h-screen font-sans uppercase">
            
            {/* HERO SECTION */}
            <section className="bg-[#2C353D] pt-32 pb-24 relative overflow-hidden text-center">
                <div className="absolute top-0 right-0 w-full h-full opacity-10 pointer-events-none">
                    <div className="grid grid-cols-6 h-full w-full">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="border-r border-white/20" />
                        ))}
                    </div>
                </div>
                
                <div className="max-w-4xl mx-auto px-6 relative z-10">
                    <motion.div
                        initial={{ scaleX: 0, opacity: 0 }}
                        animate={{ scaleX: 1, opacity: 1 }}
                        transition={{ duration: 1.0, ease: "easeOut" }}
                        className="w-24 h-[2px] bg-[#EA723D] mx-auto mb-8 shadow-[0_0_15px_rgba(234,114,61,0.5)]"
                    />
                    <motion.h1 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="text-4xl md:text-6xl font-serif text-white mb-6 leading-tight"
                    >
                        L'Accompagnement <span className="text-[#EA723D] italic">sur mesure</span>
                    </motion.h1>
                    <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                        className="text-lg md:text-xl text-white/70 font-light leading-relaxed max-w-2xl mx-auto normal-case"
                    >
                        De la première estimation jusqu'à la remise des clés, découvrez chaque étape de notre accompagnement en toute transparence.
                    </motion.p>
                </div>
            </section>

            {/* PROCESSUS INFOGRAPHIE */}
            <section className="py-24 relative z-10 overflow-hidden" style={{ background: 'linear-gradient(170deg, #f5ede4 0%, #ede6dc 60%, #f0ebe3 100%)' }}>
                <div className="max-w-3xl mx-auto px-6 relative">
                    <motion.div
                        variants={fadeUp}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                        className="text-center mb-16"
                    >
                        <p className="text-[10px] tracking-[0.5em] text-[#EA723D] uppercase mb-6 font-black">Notre engagement</p>
                        <h2 className="text-3xl md:text-4xl serif font-normal normal-case text-[#1a1a1f] leading-snug">
                            Un suivi <span className="text-[#EA723D] italic">professionnel et personnalisé</span>,<br/>de A à Z
                        </h2>
                        <div className="w-12 h-[2px] bg-[#EA723D]/40 mx-auto mt-8"></div>
                    </motion.div>

                    <div className="flex flex-col gap-5">
                        {processList.map((item, idx) => {
                            const flip = idx % 2 !== 0;
                            return (
                                <motion.div
                                    key={idx}
                                    variants={fadeIn}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, margin: "-60px" }}
                                    transition={{ duration: 0.7, delay: idx * 0.12 }}
                                    className="group flex flex-col md:flex-row items-stretch gap-0 bg-white rounded-2xl overflow-hidden shadow-sm border border-black/5 hover:shadow-xl hover:shadow-[#EA723D]/5 hover:border-[#EA723D]/20 transition-all duration-500"
                                    style={{ flexDirection: flip ? 'row-reverse' : 'row' }}
                                >
                                    {/* Bloc texte */}
                                    <div className={`flex-1 p-6 md:p-8 flex items-center gap-5 md:gap-6 border-[#EA723D] ${flip ? 'md:border-r-[4px] flex-row-reverse text-right' : 'md:border-l-[4px] border-l-[4px]'}`}>
                                        <span className="flex-shrink-0 text-5xl md:text-6xl font-black text-[#EA723D] opacity-10 font-serif leading-none transition-opacity duration-500 group-hover:opacity-20 pointer-events-none select-none">
                                            {String(idx+1).padStart(2,'0')}
                                        </span>
                                        <div>
                                            <span className="text-[10px] font-black tracking-[0.35em] text-[#EA723D] uppercase block mb-2">{item.title}</span>
                                            <p className="normal-case text-[#1a1a1f]/70 font-medium leading-relaxed text-sm md:text-[15px] group-hover:text-[#1a1a1f] transition-colors">{item.text}</p>
                                        </div>
                                    </div>
                                    
                                    {/* Bloc image */}
                                    <div className="w-full md:w-48 h-32 md:h-auto flex-shrink-0 overflow-hidden relative" style={{ background: '#f0e8de' }}>
                                        <div className="absolute inset-0 bg-black/10 mix-blend-multiply group-hover:bg-transparent transition-colors duration-500 z-10"></div>
                                        <img src={item.img} alt={item.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-1000" />
                                    </div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* PRESTATIONS ADDITIONNELLES (Le Camion) */}
            <section className="py-24 bg-white border-y border-gray-100 overflow-hidden uppercase">
                <div className="max-w-6xl mx-auto px-6">
                    <div className="bg-[#2C353D] rounded-[40px] shadow-2xl shadow-[#2C353D]/20 p-8 md:p-12 lg:p-16 relative">
                        <div className="grid lg:grid-cols-2 gap-12 items-center">
                            {/* TEXTE */}
                            <div className="flex flex-col justify-center relative z-10">
                                <motion.span 
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    className="bg-[#EA723D]/20 text-[#EA723D] border border-[#EA723D]/30 text-[10px] font-black px-4 py-1.5 rounded-full uppercase tracking-widest mb-8 inline-block w-fit"
                                >
                                    Prestation Exclusive
                                </motion.span>
                                <h2 className="text-4xl md:text-5xl font-serif text-white mb-8 leading-tight">Le Camion de <br/><span className="text-[#EA723D] italic">Déménagement</span></h2>
                                <p className="text-white/70 text-lg mb-10 leading-relaxed font-light normal-case">
                                    Plus qu'une simple transaction, nous vous accompagnons dans votre changement de vie. Profitez <strong className="text-white">gratuitement</strong> de notre véhicule utilitaire pour votre emménagement.
                                </p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-4">
                                    <div className="flex items-center gap-4 group">
                                        <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#EA723D]/20 group-hover:border-[#EA723D]/30 transition-all">
                                            <CheckCircle2 size={18} className="text-[#EA723D]" />
                                        </div>
                                        <span className="text-white/90 text-sm font-medium">Vendeurs & acquéreurs</span>
                                    </div>
                                    <div className="flex items-center gap-4 group">
                                        <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#EA723D]/20 group-hover:border-[#EA723D]/30 transition-all">
                                            <CheckCircle2 size={18} className="text-[#EA723D]" />
                                        </div>
                                        <span className="text-white/90 text-sm font-medium">Sur simple réservation</span>
                                    </div>
                                </div>
                            </div>
                            
                            {/* IMAGE */}
                            <div className="relative">
                                <motion.div 
                                    initial={{ scale: 0.95, opacity: 0 }}
                                    whileInView={{ scale: 1, opacity: 1 }}
                                    transition={{ duration: 0.8 }}
                                    className="relative rounded-[2rem] overflow-hidden aspect-[4/3] md:aspect-[3/2] shadow-2xl border-4 border-white/5"
                                >
                                    <img 
                                        src={vanBorbiconi} 
                                        alt="Camion Borbiconi" 
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-black/10 hover:bg-transparent transition-colors duration-500"></div>
                                </motion.div>
                                <div className="absolute -inset-4 bg-[#EA723D]/10 blur-3xl -z-10 rounded-full opacity-50"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CALL TO ACTION */}
            <section className="py-24 text-center bg-[#fcfaf8]">
                <div className="max-w-3xl mx-auto px-6">
                    <h2 className="text-3xl md:text-4xl font-serif text-[#2C353D] mb-6 leading-tight">Prêt à entamer votre parcours <br/>avec <span className="text-[#EA723D] italic">notre équipe</span> ?</h2>
                    <p className="text-[#2C353D]/60 text-lg mb-10 font-light max-w-xl mx-auto normal-case">
                        Notre équipe est à votre disposition pour analyser votre projet immobilier, sans engagement.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <motion.button 
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => window.location.hash = '/estimation'}
                            className="bg-[#EA723D] text-white px-10 py-4 rounded-xl text-xs font-black tracking-[0.2em] uppercase shadow-xl shadow-[#EA723D]/30 hover:bg-[#d6602d] transition-all w-full sm:w-auto"
                        >
                            Estimer mon bien
                        </motion.button>
                        <motion.button 
                            whileHover={{ scale: 1.05, y: -2 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => window.location.hash = '/honoraires'}
                            className="bg-transparent text-[#2C353D] border-2 border-[#2C353D]/20 px-10 py-4 rounded-xl text-xs font-black tracking-[0.2em] uppercase hover:bg-[#2C353D]/5 transition-all w-full sm:w-auto"
                        >
                            Nos honoraires
                        </motion.button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Process;
